import { useEffect, useMemo, useState } from 'react'
import { Spin, Empty, Table, Select, DatePicker } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import dayjs from 'dayjs'
import type { Dayjs } from 'dayjs'
import { getStockOpsFinancials } from '@/api/stock'
import type { OpsFinancialRow } from '@/api/stock'

/** 原始字段名 → 中文标签（覆盖 ops 美股 us_xbrl / 港 A report_metric 常用字段） */
const FIELD_LABELS: Record<string, string> = {
    // 身份 / 元数据
    stock_code: '股票代码', code: '代码', ticker: '代码', market: '市场', currency: '币种',
    company_name: '公司名', report_type: '报告类型', form_type: '申报类型', filing_date: '披露日',
    period_start: '期初', period_start_date: '期初', period_end: '期末', period_end_date: '期末',
    report_date: '报告日', fiscal_year: '财年', source_file_id: '源文件ID', amount_unit: '金额单位',
    industry_profile: '行业档案',
    // 损益
    revenue: '营业收入', revenue_product: '产品收入', revenue_service: '服务收入',
    cost_of_revenue: '营业成本', gross_profit: '毛利',
    selling_general_and_administrative_expense: '销售管理费用', research_and_development_expense: '研发费用',
    operating_expenses: '营业费用', depreciation_and_amortization: '折旧摊销', ebitda: 'EBITDA',
    operating_income: '营业利润', pretax_income: '税前利润', income_tax_expense: '所得税',
    net_income: '净利润', net_income_parent: '归母净利润', net_income_nci: '少数股东损益',
    interest_expense: '利息支出', interest_income: '利息收入', other_nonoperating_income: '其他非营业收益',
    eps_basic: '基本EPS', eps_diluted: '稀释EPS',
    weighted_avg_shares_basic: '加权股本(基本)', weighted_avg_shares_diluted: '加权股本(稀释)',
    // 现金流
    operating_cash_flow: '经营现金流', investing_cash_flow: '投资现金流', financing_cash_flow: '筹资现金流',
    capital_expenditure: '资本开支', free_cash_flow: '自由现金流', ending_cash: '期末现金',
    dividends_paid: '分红', total_dividends_paid: '分红总额', share_repurchases: '回购', total_share_repurchases: '回购总额',
    stock_comp_addback: 'SBC加回', da_addback: 'D&A加回',
    change_in_receivables: '应收账款变动', change_in_inventory: '存货变动', change_in_payables: '应付账款变动',
    acquisitions_net: '并购净额', proceeds_from_investments: '投资所得', debt_proceeds: '举债所得', debt_repayments: '偿债',
    // 资产负债
    total_assets: '总资产', total_liabilities: '总负债', total_equity: '股东权益',
    cash_and_equivalents: '现金及等价物', accounts_receivable: '应收账款', inventory: '存货', goodwill: '商誉',
    total_current_assets: '流动资产', total_current_liabilities: '流动负债', shares_outstanding: '总股本',
    restricted_cash: '受限现金', short_term_investments: '短期投资', prepaid_expenses: '预付费用',
    property_plant_equipment_net: '固定资产净值', right_of_use_assets: '使用权资产', intangible_assets_net: '无形资产净值',
    long_term_investments: '长期投资', accounts_payable: '应付账款', accrued_liabilities: '应计负债',
    deferred_revenue_current: '递延收入(流动)', short_term_debt: '短期债务', long_term_debt: '长期债务',
    operating_lease_liability: '经营租赁负债', deferred_tax_assets: '递延税资产', deferred_tax_liabilities: '递延税负债',
    retained_earnings: '留存收益', accumulated_other_comprehensive_income: '累计其他综合收益', treasury_stock: '库藏股',
    // 派生比率 / 同比
    gross_margin: '毛利率', roe: 'ROE', roa: 'ROA', debt_to_asset: '资产负债率',
    quick_ratio: '速动比率', asset_turnover: '资产周转率', receivables_days: '应收账款天数', inventory_days: '存货天数',
    rd_ratio: '研发占比', cash_content_of_revenue: '收入现金含量', cf_ni_ratio: '现金流/净利',
    period_expense_ratio: '期间费用率', payout_ratio: '派息率', bvps: '每股净资产',
    interest_bearing_liabilities: '有息负债', revenue_yoy: '收入同比', operating_income_yoy: '营业利润同比',
    net_income_yoy: '净利润同比', revenue_qoq: '收入环比', operating_income_qoq: '营业利润环比',
    net_income_qoq: '净利润环比', effective_tax_rate: '有效税率',
    // QG10 盈利清洗成分
    acquired_intangibles_amortization: '并购无形资产摊销', investment_gains_losses: '投资收益/损失', one_off_items: '一次性项目',
}

/** 身份/元数据列优先排前面，其余按字段名字典序 */
const IDENTITY_ORDER = [
    // 用户指定的优先展示顺序：股票代码、公司名、报告类型、报告日、期初、期末、财年
    'stock_code', 'company_name', 'report_type', 'report_date',
    'period_start', 'period_end', 'fiscal_year',
    // 备用字段名，仍优先于其余字段，排在主字段之后
    'code', 'ticker', 'form_type', 'filing_date',
    'period_start_date', 'period_end_date', 'currency', 'market',
]

/** 年份类字段（财年等）直接取整数展示，不保留小数 */
const YEAR_FIELDS = new Set(['fiscal_year', 'year'])

const LIMIT_OPTIONS = [4, 8, 12, 20, 50]

function isNumericColumn(rows: OpsFinancialRow[], field: string): boolean {
    let seen = false
    for (const r of rows) {
        const v = r[field]
        if (v === null || v === undefined || v === '') continue
        seen = true
        if (typeof v === 'object' || typeof v === 'boolean') return false
        if (!Number.isFinite(Number(v))) return false
    }
    return seen
}

function fmtBig(v: unknown, field?: string): string {
    if (typeof v === 'boolean') return v ? 'true' : 'false'
    if (typeof v === 'object') {
        const s = JSON.stringify(v)
        return s.length > 40 ? s.slice(0, 40) + '…' : s
    }
    const n = Number(v)
    if (!Number.isFinite(n)) return String(v)
    if (field && YEAR_FIELDS.has(field)) return String(Math.round(n))
    if (Math.abs(n) >= 1e8) return (n / 1e8).toFixed(1) + '亿'
    if (Math.abs(n) >= 1e4) return (n / 1e4).toFixed(1) + '万'
    return n.toFixed(2)
}

function Cell({ value, field }: { value: unknown; field?: string }) {
    if (value === null || value === undefined || value === '') {
        return <span style={{ color: '#fa541c', fontWeight: 600 }}>缺</span>
    }
    return <>{fmtBig(value, field)}</>
}

export default function OpsFinancials({ code }: { code: string }) {
    const [items, setItems] = useState<OpsFinancialRow[]>([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState('')
    const [limit, setLimit] = useState(12)
    const [asOf, setAsOf] = useState<string | null>(null)

    useEffect(() => {
        if (!code) return
        setLoading(true)
        setError('')
        getStockOpsFinancials(code, limit, asOf ?? undefined)
            .then(res => setItems(Array.isArray(res.items) ? res.items : []))
            .catch(e => setError(e instanceof Error ? e.message : '加载失败'))
            .finally(() => setLoading(false))
    }, [code, limit, asOf])

    const columns: ColumnsType<OpsFinancialRow> = useMemo(() => {
        const keySet = new Set<string>()
        items.forEach(r => Object.keys(r).forEach(k => keySet.add(k)))
        const keys = Array.from(keySet)
        keys.sort((a, b) => {
            const ia = IDENTITY_ORDER.indexOf(a)
            const ib = IDENTITY_ORDER.indexOf(b)
            if (ia !== -1 || ib !== -1) {
                if (ia === -1) return 1
                if (ib === -1) return -1
                return ia - ib
            }
            return a.localeCompare(b)
        })
        return keys.map(field => {
            const label = FIELD_LABELS[field]
            const numeric = isNumericColumn(items, field)
            return {
                title: label ? `${field}（${label}）` : field,
                key: field,
                width: numeric ? 130 : 180,
                align: numeric ? 'right' : 'left',
                render: (_: unknown, row: OpsFinancialRow) => <Cell value={row[field]} field={field} />,
            }
        })
    }, [items])

    const onAsOfChange = (d: Dayjs | null) => {
        setAsOf(d ? d.format('YYYY-MM-DD') : null)
    }

    return (
        <div style={{ marginTop: 16 }}>
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 8,
                marginBottom: 8,
            }}>
                <span style={{ fontSize: 14, fontWeight: 600 }}>ops 财报原始数据</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 12, color: 'var(--text-3)' }}>截止日期</span>
                    <DatePicker
                        value={asOf ? dayjs(asOf) : null}
                        size="small"
                        style={{ width: 130 }}
                        placeholder="不限"
                        format="YYYY-MM-DD"
                        allowClear
                        onChange={onAsOfChange}
                    />
                    <span style={{ fontSize: 12, color: 'var(--text-3)' }}>查询期数</span>
                    <Select
                        value={limit}
                        size="small"
                        style={{ width: 90 }}
                        options={LIMIT_OPTIONS.map(n => ({ value: n, label: `${n} 期` }))}
                        onChange={v => setLimit(v)}
                    />
                    <span style={{ fontSize: 12, color: 'var(--text-3)' }}>
                        {items.length} 期 · {columns.length} 字段 · <span style={{ color: '#fa541c' }}>缺</span>=该字段该期缺失
                    </span>
                </div>
            </div>

            {loading ? (
                <div style={{ padding: '40px 0', textAlign: 'center' }}><Spin /></div>
            ) : error ? (
                <div style={{ padding: '32px 0', textAlign: 'center' }}><Empty description={error} /></div>
            ) : !items.length ? (
                <div style={{ padding: '32px 0', textAlign: 'center' }}><Empty description="暂无 ops 财报数据" /></div>
            ) : (
                <Table
                    columns={columns}
                    dataSource={items}
                    rowKey={(_, idx) => String(idx)}
                    pagination={false}
                    size="small"
                    scroll={{ x: 'max-content' }}
                />
            )}
        </div>
    )
}
